import React, { useState, useMemo, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Mic2, Plus, Trash2, X } from "lucide-react";
import DeleteModel from "./DeleteModel";
import PriestApi from "../api/priestApi";
import "../assets/css/rosterActionModals.css";

function AssignTalkModal({
  show,
  handleClose,
  allRoasters = [],
  talksDataList = [],
  retreatRoastApi,
  retreatId,
  onAssignSuccess,
  spritualDirector,
  isTemp,
}) {
  const talkOrder = [
    "Holy Matrimony: God, Husband & Wife",
    "The Sacrament of Marriage",
    "Understanding Yourself, Understanding Your Spouse",
    "Communication",
    "Challenges in Marriage",
    "Children and In-Laws",
    "Sex and Marriage",
    "Marital Bliss",
    "Forgiveness",
    "Us vs. the World",
    "Go Forth With God",
  ];
  const [assignments, setAssignments] = useState([]);
  const [selectedTalk, setSelectedTalk] = useState("");
  const [selectedRoster, setSelectedRoster] = useState("");
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  // ── ADD THESE TWO STATE VARIABLES ONLY ───────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingRemoveRosterId, setPendingRemoveRosterId] = useState(null);
  // ─────────────────────────────────────────────────────────────────────────

  const priestApi = useMemo(() => new PriestApi(), []);
  const [priestsList, setPriestsList] = useState([]);
  const [selectedPriest, setSelectedPriest] = useState("");

  useEffect(() => {
    if (show) {
      const existing = allRoasters
        .filter((r) => r.talkId != null)
        .map((r) => {
          const talk = talksDataList.find(
            (t) => Number(t.talkId) === Number(r.talkId),
          );
          return {
            talkId: Number(r.talkId),
            talkName: talk ? talk.talk : "",
            rosterId: r.id,
            coupleLabel: buildCoupleLabel(r),
            hisEmail: r.hisEmail || null,
            herEmail: r.herEmail || null,
          };
        });
      setAssignments(existing);

      const fetchPriests = async () => {
        try {
          const res = await priestApi.getAllPriests({ pageNo: 1, size: 100, export: true });
          if (res.success && res.data) {
            setPriestsList(res.data);
          }
        } catch (err) {
          console.error("Failed to fetch priests", err);
        }
      };
      fetchPriests();
    }
  }, [show, allRoasters, talksDataList]);

  const priestRow = useMemo(() => {
    if (!spritualDirector || !spritualDirector.talk || !spritualDirector.talk.id) return null;
    const talk = spritualDirector.talk;
    return {
      talkId: Number(talk.id),
      talkName: talk.talk,
      rosterId: spritualDirector.rosterId || null,
      coupleLabel: `${spritualDirector.primaryKey ? spritualDirector.primaryKey + " – " : ""}${spritualDirector.title ? spritualDirector.title + " " : ""}${spritualDirector.firstName} ${spritualDirector.lastName || ""}`,
      priestEmail: spritualDirector.email || null,
      isPriest: true,
    };
  }, [spritualDirector]);

  const assignedTalkIds = useMemo(() => {
    const ids = new Set(assignments.map((a) => a.talkId));
    if (priestRow && priestRow.talkId) {
      ids.add(priestRow.talkId);
    }
    return ids;
  }, [assignments, priestRow]);

  const assignedRosterIds = useMemo(
    () => new Set(assignments.map((a) => a.rosterId)),
    [assignments],
  );

  const selectedTalkObj = useMemo(() => {
    return talksDataList.find((t) => String(t.talkId) === String(selectedTalk));
  }, [selectedTalk, talksDataList]);

  const isSacramentSelected = selectedTalkObj && selectedTalkObj.talk === "The Sacrament of Marriage";

  const priestOptions = useMemo(() => {
    return priestsList
      .map((p) => ({
        id: p.id,
        label: `${p.title ? p.title + " " : ""}${p.firstName} ${p.lastName || p.name || ""} - ${p.email || ""}`.trim(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [priestsList]);

  const availableTalks = useMemo(() => {
    return talksDataList
      .filter((t) => !assignedTalkIds.has(Number(t.talkId)))
      .sort((a, b) => {
        const indexA = talkOrder.indexOf(a.talk);
        const indexB = talkOrder.indexOf(b.talk);
        if (indexA === -1 && indexB === -1) return a.talk.localeCompare(b.talk);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
  }, [talksDataList, assignedTalkIds]);

  const availableCouples = useMemo(
    () =>
      allRoasters
        .filter((r) => !assignedRosterIds.has(r.id))
        .map((r) => ({ ...r, label: buildCoupleLabel(r) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allRoasters, assignedRosterIds],
  );

  function buildCoupleLabel(r) {
    const pk = r.primaryKey ? `${r.primaryKey} – ` : "";
    const his = [r.hisLastName, r.hisFirstName].filter(Boolean).join(", ");
    const her = r.herFirstName || "";
    return `${pk}${his}${her ? ` & ${her}` : ""}`;
  }

  const handleAdd = async () => {
    if (!selectedTalk || loadingAdd) return;
    if (isSacramentSelected && !selectedRoster && !selectedPriest) return;
    if (!isSacramentSelected && !selectedRoster) return;

    const talk = talksDataList.find(
      (t) => String(t.talkId) === String(selectedTalk),
    );
    if (!talk) return;

    setLoadingAdd(true);
    try {
      let payload = {
        assignments: [],
        retreatId: retreatId,
        isTemp: isTemp,
      };

      if (isSacramentSelected && selectedPriest) {
        payload.assignments.push({
          priestId: Number(selectedPriest),
          talkId: Number(talk.talkId),
        });
      } else {
        const couple = allRoasters.find(
          (r) => String(r.id) === String(selectedRoster),
        );
        if (!couple) {
          setLoadingAdd(false);
          return;
        }
        payload.assignments.push({
          rosterId: couple.id,
          talkId: Number(talk.talkId),
        });
      }

      const res = await retreatRoastApi.updateRoasterTalk(payload);

      if (res.success) {
        setSelectedTalk("");
        setSelectedRoster("");
        setSelectedPriest("");
        if (onAssignSuccess) onAssignSuccess();
      } else {
        alert(res.msg || "Failed to assign talk. Please try again.");
      }
    } catch (err) {
      alert(err?.message || "An error occurred while assigning the talk.");
    } finally {
      setLoadingAdd(false);
    }
  };

  // ── CHANGE 2: Split handleRemove into confirmRemove + handleRemove ────────
  const confirmRemove = (rosterId) => {
    setPendingRemoveRosterId(rosterId);
    setShowDeleteModal(true);
  };

  const handleRemove = async () => {
    const rosterId = pendingRemoveRosterId;
    setShowDeleteModal(false);
    setPendingRemoveRosterId(null);

    if (!rosterId || removingId === rosterId) return;

    setRemovingId(rosterId);
    try {
      const res = await retreatRoastApi.updateRoasterTalk({
        assignments: [{ rosterId, talkId: null }],
        retreatId: retreatId,
        isTemp: isTemp,
      });

      if (res.success) {
        setAssignments((prev) => prev.filter((a) => a.rosterId !== rosterId));
        if (onAssignSuccess) onAssignSuccess();
      } else {
        alert(res.msg || "Failed to remove talk. Please try again.");
      }
    } catch (err) {
      alert(err?.message || "An error occurred while removing the talk.");
    } finally {
      setRemovingId(null);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  const handleModalClose = () => {
    setSelectedTalk("");
    setSelectedRoster("");
    setSelectedPriest("");
    handleClose();
  };

  const canAdd =
    selectedTalk &&
    (isSacramentSelected ? (selectedRoster || selectedPriest) : selectedRoster) &&
    !loadingAdd;

  const assignedCount = assignments.length + (priestRow ? 1 : 0);

  return (
    <>
      <Modal
        show={show}
        onHide={handleModalClose}
        centered
        className="ram-modal"
        dialogClassName="ram-dialog ram-dialog-wide"
        contentClassName="ram-content"
      >
        <div className="ram-shell">
        <header className="ram-header">
          <div className="ram-mark ram-mark--violet" aria-hidden="true">
            <Mic2 size={20} />
          </div>
          <div className="ram-header-copy">
            <p className="ram-kicker">Retreat talks</p>
            <h2>Assign talks</h2>
            <p>Select an unassigned talk and a couple, then click Assign.</p>
          </div>
          <button
            type="button"
            className="ram-close"
            onClick={handleModalClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>
        <div className="ram-body">
          <section className="ram-panel">
            <p className="ram-help">
              Select an unassigned talk and a couple, then click{" "}
              <strong>Assign</strong>. The Sacrament of Marriage can go to a
              couple or a priest.
            </p>

            <div
              className={`ram-assign-form${isSacramentSelected ? " is-priest" : ""}`}
            >
              <div className="ram-field">
                <label htmlFor="assign-talk">Talk</label>
                <select
                  id="assign-talk"
                  value={selectedTalk}
                  onChange={(e) => {
                    setSelectedTalk(e.target.value);
                    setSelectedRoster("");
                    setSelectedPriest("");
                  }}
                  disabled={loadingAdd}
                >
                  <option value="">Select a talk</option>
                  {availableTalks.length === 0 && (
                    <option disabled>All talks assigned</option>
                  )}
                  {availableTalks.map((t) => (
                    <option key={t.talkId} value={t.talkId}>
                      {t.talk}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ram-field">
                <label htmlFor="assign-couple">Couple</label>
                <select
                  id="assign-couple"
                  value={selectedRoster}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedRoster(val);
                    if (val) {
                      setSelectedPriest("");
                    }
                  }}
                  disabled={loadingAdd}
                >
                  <option value="">Select a Couple</option>
                  {availableCouples.length === 0 && (
                    <option disabled>All Couples assigned</option>
                  )}
                  {availableCouples.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              {isSacramentSelected && (
                <div className="ram-field">
                  <label htmlFor="assign-priest">Priest</label>
                  <select
                    id="assign-priest"
                    value={selectedPriest}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedPriest(val);
                      if (val) {
                        setSelectedRoster("");
                      }
                    }}
                    disabled={loadingAdd}
                  >
                    <option value="">Select a priest</option>
                    {priestOptions.length === 0 && (
                      <option disabled>No priests found</option>
                    )}
                    {priestOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <button
                type="button"
                className={`ram-btn ram-btn-primary${loadingAdd ? " is-busy" : ""}`}
                onClick={handleAdd}
                disabled={!canAdd}
                title="Assign talk"
              >
                {loadingAdd ? (
                  <span className="ram-spinner" />
                ) : (
                  <Plus size={16} />
                )}
                {loadingAdd ? "Saving..." : "Assign"}
              </button>
            </div>
          </section>

          <section className="ram-assigned">
            <div className="ram-assigned-head">
              <h3>Assigned talks</h3>
              <span className="ram-count">{assignedCount}</span>
            </div>

            {assignments.length === 0 && !priestRow ? (
              <div className="ram-empty">
                <strong>No talks assigned yet</strong>
                Use the selectors above to match a talk with a couple.
              </div>
            ) : (
              <div className="ram-list">
                <div className="ram-row is-head">
                  <span>Talk</span>
                  <span>Couple / priest</span>
                  <span>Action</span>
                </div>

                {priestRow && (
                  <div className="ram-row">
                    <span>
                      <span className="ram-talk is-priest">
                        {priestRow.talkName}
                      </span>
                    </span>
                    <span className="ram-person">
                      <strong>{priestRow.coupleLabel}</strong>
                      {priestRow.priestEmail ? (
                        <small>{priestRow.priestEmail}</small>
                      ) : null}
                      <span className="ram-badge">Spiritual Director</span>
                    </span>
                    <span>
                      {priestRow.talkId ? (
                        <button
                          type="button"
                          className="ram-icon-btn"
                          onClick={() => confirmRemove(priestRow.rosterId)}
                          disabled={
                            removingId === priestRow.rosterId || loadingAdd
                          }
                          title="Remove assigned talk"
                          aria-label="Remove assigned talk"
                        >
                          <Trash2 size={15} />
                        </button>
                      ) : (
                        <span className="ram-empty-inline">—</span>
                      )}
                    </span>
                  </div>
                )}

                {assignments.map((a) => {
                  const isRemoving = removingId === a.rosterId;
                  return (
                    <div
                      key={a.rosterId}
                      className={`ram-row${isRemoving ? " is-muted" : ""}`}
                    >
                      <span>
                        <span className="ram-talk">{a.talkName}</span>
                      </span>
                      <span className="ram-person">
                        <span>{a.coupleLabel}</span>
                        {(a.hisEmail || a.herEmail) && (
                          <small>
                            {[a.hisEmail, a.herEmail]
                              .filter(Boolean)
                              .join(" & ")}
                          </small>
                        )}
                      </span>
                      <span>
                        <button
                          type="button"
                          className="ram-icon-btn"
                          onClick={() => confirmRemove(a.rosterId)}
                          disabled={isRemoving || loadingAdd}
                          title="Remove assigned talk"
                          aria-label="Remove assigned talk"
                        >
                          <Trash2 size={15} />
                        </button>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <footer className="ram-footer">
          <button
            type="button"
            className="ram-btn ram-btn-quiet"
            onClick={handleModalClose}
          >
            Close
          </button>
        </footer>
        </div>
      </Modal>

      {/* ── CHANGE 2: Delete confirmation modal ── */}
      <DeleteModel
        show={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setPendingRemoveRosterId(null);
        }}
        DeleteData={handleRemove}
        content={
          <>
            <h5 className="mt-4 fw-bold">Talk Assignment</h5>
            <div className="p-3 border rounded-3 bg-light shadow-sm">
              {pendingRemoveRosterId &&
                (() => {
                  let assignment = assignments.find(
                    (a) => a.rosterId === pendingRemoveRosterId,
                  );
                  if (!assignment && priestRow && priestRow.rosterId === pendingRemoveRosterId) {
                     assignment = {
                       talkName: priestRow.talkName,
                       coupleLabel: priestRow.coupleLabel,
                     };
                  }
                  return assignment ? (
                    <>
                      <div className="mb-2">
                        <span className="fw-bold">Talk:</span>{" "}
                        <span className="text-muted">
                          {assignment.talkName}
                        </span>
                      </div>
                      <div className="mb-2">
                        <span className="fw-bold">Assignee:</span>{" "}
                        <span className="text-muted">
                          {assignment.coupleLabel}
                        </span>
                      </div>
                    </>
                  ) : null;
                })()}
            </div>
          </>
        }
      />
    </>
  );
}

export default AssignTalkModal;
